import {
  MsgPrivilegedExecuteContract,
  ExecPrivilegedArgVaultRedeem,
  ExecPrivilegedArgVaultSubscribe,
  ExecPrivilegedArgOffChainVaultRedeem,
  ExecPrivilegedArgOffChainVaultSubscribe
} from '@injectivelabs/sdk-ts'
import { msgBroadcaster } from '@shared/WalletService'
import { sharedToBalanceInWei } from '@shared/utils/formatter'
import { delayPromiseCall } from '@/app/client/utils/async'
import {
  UiVault,
  VaultSubPage,
  UiSubscription,
  VaultMarketType,
  VaultContractType,
  SpotRedemptionType,
  DerivativeRedemptionType
} from '@/types'

const fetchVaultDetails = async (vaultAddress: string) => {
  const route = useRoute()
  const mitoStore = useMitoStore()
  const portfolioStore = usePortfolioStore()

  if (Object.values(VaultSubPage).includes(route.name as VaultSubPage)) {
    await delayPromiseCall(
      () =>
        Promise.all([
          mitoStore.fetchVault(vaultAddress),
          portfolioStore.fetchAccountPortfolio(),
          mitoStore.fetchTVLPriceChart({ vaultAddress }),
          mitoStore.fetchTokenPriceChart({ vaultAddress })
        ]),
      3000
    )
  }
}

export const subscribeToDerivativeVault = async ({
  vault,
  quoteAmount
}: {
  vault: UiVault
  quoteAmount: string
}) => {
  const walletStore = useWalletStore()
  const sharedWalletStore = useSharedWalletStore()

  if (
    !sharedWalletStore.isUserConnected ||
    !sharedWalletStore.defaultSubaccountId
  ) {
    return
  }

  await walletStore.validateAndQueue()

  const slippage = {
    max_penalty: '0.1'
  }

  const data =
    vault.vaultType === VaultContractType.OffChain
      ? ExecPrivilegedArgOffChainVaultSubscribe.fromJSON({
          args: {
            slippage,
            subscriber_subaccount_id: sharedWalletStore.defaultSubaccountId
          },
          origin: sharedWalletStore.injectiveAddress
        })
      : ExecPrivilegedArgVaultSubscribe.fromJSON({
          args: { slippage },
          origin: sharedWalletStore.injectiveAddress,
          vaultSubaccountId: vault.subaccountId,
          traderSubaccountId: sharedWalletStore.defaultSubaccountId
        })

  const amount = sharedToBalanceInWei({
    value: quoteAmount,
    decimalPlaces: vault.market.quoteToken.decimals
  }).toFixed()

  const message = MsgPrivilegedExecuteContract.fromJSON({
    data,
    sender: sharedWalletStore.injectiveAddress,
    contractAddress:
      vault.vaultType === VaultContractType.OffChain
        ? vault.contractAddress
        : vault.masterContractAddress,
    funds: `${amount} ${vault.market.quoteToken.denom}`
  })

  const tx = await msgBroadcaster.broadcastV2({
    injectiveAddress: sharedWalletStore.injectiveAddress,
    msgs: message
  })

  await fetchVaultDetails(vault.contractAddress)

  return tx
}

export const subscribeToSpotVault = async ({
  vault,
  baseAmount,
  quoteAmount,
  subscriptionType
}: {
  vault: UiVault
  baseAmount: string
  quoteAmount: string
  subscriptionType: SpotRedemptionType
}) => {
  const walletStore = useWalletStore()
  const sharedWalletStore = useSharedWalletStore()

  if (
    !sharedWalletStore.injectiveAddress ||
    !sharedWalletStore.defaultSubaccountId
  ) {
    return
  }

  const slippage = {
    max_penalty: '0.1'
  }

  await walletStore.validateAndQueue()

  const data =
    vault.vaultType === VaultContractType.OffChain
      ? ExecPrivilegedArgOffChainVaultSubscribe.fromJSON({
          args: {
            subscriber_subaccount_id: sharedWalletStore.defaultSubaccountId
          },
          origin: sharedWalletStore.injectiveAddress
        })
      : ExecPrivilegedArgVaultSubscribe.fromJSON({
          args: vault.vaultType === VaultContractType.CPMM ? { slippage } : {},
          origin: sharedWalletStore.injectiveAddress,
          vaultSubaccountId: vault.subaccountId,
          traderSubaccountId: sharedWalletStore.defaultSubaccountId
        })

  const formattedBaseAmount =
    subscriptionType !== SpotRedemptionType.QuoteOnly
      ? `${sharedToBalanceInWei({
          value: baseAmount,
          decimalPlaces: vault.market.baseToken.decimals
        }).toFixed()} ${vault.market.baseToken.denom}`
      : ''

  const formattedQuoteAmount =
    subscriptionType !== SpotRedemptionType.BaseOnly
      ? `${sharedToBalanceInWei({
          value: quoteAmount,
          decimalPlaces: vault.market.quoteToken.decimals
        }).toFixed()} ${vault.market.quoteToken.denom}`
      : ''

  const funds = [formattedBaseAmount, formattedQuoteAmount]
    .filter((amount) => amount)
    .join(', ')

  const message = MsgPrivilegedExecuteContract.fromJSON({
    data,
    funds,
    sender: sharedWalletStore.injectiveAddress,
    contractAddress:
      vault.vaultType === VaultContractType.OffChain
        ? vault.contractAddress
        : vault.masterContractAddress
  })

  const tx = await msgBroadcaster.broadcastV2({
    injectiveAddress: sharedWalletStore.injectiveAddress,
    msgs: message
  })

  await fetchVaultDetails(vault.contractAddress)

  return tx
}

export const redeem = async ({
  subscription,
  redemptionType,
  lpTokenBurnAmount
}: {
  lpTokenBurnAmount: string
  subscription: UiSubscription
  redemptionType: SpotRedemptionType | DerivativeRedemptionType
}) => {
  const walletStore = useWalletStore()
  const sharedWalletStore = useSharedWalletStore()

  if (
    !sharedWalletStore.isUserConnected ||
    !sharedWalletStore.defaultSubaccountId
  ) {
    return
  }

  await walletStore.validateAndQueue()

  const slippage = {
    max_penalty: '0.1'
  }

  const data =
    subscription.vaultInfo.vaultType === VaultContractType.OffChain
      ? ExecPrivilegedArgOffChainVaultRedeem.fromJSON({
          origin: sharedWalletStore.injectiveAddress,
          args: {
            ...(subscription.vaultInfo.marketType === VaultMarketType.Derivative
              ? {
                  slippage,
                  redemption_type: { DerivativeRedemptionType: redemptionType }
                }
              : { redemption_type: { SpotRedemptionType: redemptionType } }),
            redeemer_subaccount_id: sharedWalletStore.defaultSubaccountId
          }
        })
      : ExecPrivilegedArgVaultRedeem.fromJSON({
          origin: sharedWalletStore.injectiveAddress,
          traderSubaccountId: sharedWalletStore.defaultSubaccountId,
          vaultSubaccountId: subscription.vaultInfo.subaccountId,
          args: {
            ...(subscription.vaultInfo.marketType === VaultMarketType.Derivative
              ? { slippage }
              : {}),
            redemption_type: redemptionType
          }
        })

  const formattedLpBurnAmount = sharedToBalanceInWei({
    value: lpTokenBurnAmount
  }).toFixed()

  const message = MsgPrivilegedExecuteContract.fromJSON({
    data,
    sender: sharedWalletStore.injectiveAddress,
    contractAddress:
      subscription.vaultInfo.vaultType === VaultContractType.OffChain
        ? subscription.vaultInfo.contractAddress
        : subscription.vaultInfo.masterContractAddress,
    funds: `${formattedLpBurnAmount} ${subscription.vaultInfo.lpDenom}`
  })

  const tx = await msgBroadcaster.broadcastV2({
    injectiveAddress: sharedWalletStore.injectiveAddress,
    msgs: message
  })

  await fetchVaultDetails(subscription.vaultInfo.contractAddress)

  return tx
}
